import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { OnboardingButtonPrimary } from "../components/OnboardingButton";
import OnboardingDropdown from "../components/OnboardingDropdown";
import { OnboardingInput } from "../components/OnboardingInput";
import OnboardingGetCompanyInfo from "../components/OnboardingGetCompanyInfo";
import OnboardingHelpLink from "../components/OnboardingHelpLink";
import { HeadingLarge, LabelMedium } from "../components/OnboardingTypography";
import { useFinancialSystems } from "../hooks/useFinancialSystems";
import ExternalOnboardingInfo from "../components/ExternalOnboardingInfo";
import {  FlexColumn,  MessageBox,  THEME,} from "../components/OnboardingLayoutComponents";

const OnboardingPage1 = ({
  selectedFinancialSystem,
  setSelectedFinancialSystem,
  otherFinancialSystemName,
  setOtherFinancialSystemName,
  orgNr,
  setOrgNr,
  companyName,
  setCompanyName,
  adress,
  setAdress,
  postnummer,
  setPostnummer,
  poststed,
  setPoststed,
  goToNextStep,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const {
    financialSystems,
    loading: financialSystemsLoading,
    error: financialSystemsError,
    getSystemDisplayName,
    isExternalSystem,
    getExternalUrl,
  } = useFinancialSystems();

  const systemDisplayName = getSystemDisplayName(selectedFinancialSystem);
  const showExternalInfo = isExternalSystem(selectedFinancialSystem, systemDisplayName);
  const externalUrl = showExternalInfo ? getExternalUrl(systemDisplayName) : null;

  const isNextButtonDisabled =
    !isFormValid ||
    selectedFinancialSystem === 0 || // Disable if "Velg" is selected
    isEditing ||
    financialSystemsLoading;

  return (
    <div>
      <FlexColumn
        gap={THEME.spacing.xxxl}
        padding={"0 0 " + THEME.spacing.mega + " 0"}
      >
        <HeadingLarge style={{ paddingTop: THEME.spacing.xxl }}>
          <FormattedMessage id="step1Title" />
        </HeadingLarge>
        <FlexColumn gap={THEME.spacing.xxl}>
          <LabelMedium>
            <FormattedMessage id="step1Description1" />
          </LabelMedium>
          <LabelMedium>
            <FormattedMessage id="step1Description2" />
          </LabelMedium>

          <OnboardingHelpLink />
        </FlexColumn>

      {financialSystemsError && (
        <MessageBox variant="error">{financialSystemsError}</MessageBox>
      )}

      <OnboardingDropdown
        label={<FormattedMessage id="financialSystemLabel" />}
        items={
          financialSystemsLoading
            ? [{ value: 0, label: <FormattedMessage id="loadingText" /> }]
            : financialSystems
        }
        onChange={(e) => {
          const value = e.target.value;
          // Convert string values back to proper types
          if (value === "null") {
            setSelectedFinancialSystem(null);
          } else if (value === "0") {
            setSelectedFinancialSystem(0);
          } else {
            // Convert numeric system IDs back to numbers
            const numericValue = parseInt(value, 10);
            setSelectedFinancialSystem(
              isNaN(numericValue) ? value : numericValue
            );
          }
        }}
        defaultValue={
          selectedFinancialSystem === null ? "null" : selectedFinancialSystem
        }
        disabled={financialSystemsLoading}
      />

      {selectedFinancialSystem === null && (
        <OnboardingInput
          label={<FormattedMessage id="otherFinancialSystemLabel" />}
          value={otherFinancialSystemName}
          onChange={(e) => setOtherFinancialSystemName(e.target.value)}
          type="text"
          required={false}
        />
      )}

      {showExternalInfo ? (
        <ExternalOnboardingInfo
          selectedFinancialSystem={selectedFinancialSystem}
          systemDisplayName={systemDisplayName}
          externalUrl={externalUrl}
        />
      ) : (
        <FlexColumn gap={THEME.spacing.xxl}>
          <OnboardingGetCompanyInfo
            orgNr={orgNr}
            setOrgNr={setOrgNr}
            companyName={companyName}
            setCompanyName={setCompanyName}
            adress={adress}
            setAdress={setAdress}
            postnummer={postnummer}
            setPostnummer={setPostnummer}
            poststed={poststed}
            setPoststed={setPoststed}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onValidationChange={setIsFormValid}
          />

          <FlexColumn
            margin="16px 0 0 0"
            style={{ display: isNextButtonDisabled ? "none" : "block" }}
          >
            <OnboardingButtonPrimary type="button" onClick={goToNextStep}>
              <LabelMedium>
                <FormattedMessage id="nextButton" />
              </LabelMedium>
            </OnboardingButtonPrimary>
          </FlexColumn>
        </FlexColumn>
      )}
      </FlexColumn>
    </div>
  );
};

export default OnboardingPage1;
